import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Box, Container, Grid, Typography, Stack } from "@mui/material";
import {
  AccountBalanceWalletOutlined as WalletIcon,
  ContentCopy as CopyIcon,
  Share as ShareIcon,
  TrendingUp as TrendingIcon,
  CasinoOutlined as LotteryIcon,
  CardGiftcardOutlined as GiftCardIcon,
  SwapHoriz as SwapIcon,
  StarsOutlined as StarsIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import { useHistory, Link } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Real Web3 Contract Services (Existing & Previously Mapped)
import cinstance from "../../Service/randomcinstance";
import usercinstance from "../../Service/usercinstance";
import tokencinstance from "../../Service/tokencinstance";
import web3 from "../../Service/web3";
import { networkID } from "../../../static/utils";
import RegisterAsorganisation from "../User/RegisterAsOrganisation";
import { colors, customShadows, borderRadiusPx } from "../../../theme";
import { Path, getNetworkCurrency } from "../../../static/Constants";

// Reusable Dashboard Material UI Components
import {
  CreatorLayout,
  MetricCard,
  DashboardNoticeBanner,
  RevenueBreakdownCard,
  PositionSummaryCard,
  WalletTreasuryCard,
  CreatorSummaryActionsCard,
  TopPerformingTableCard,
  BottomMetricsRibbon,
} from "../../Components/dashboard";
import {
  CommonButton,
  CommonDialog,
  CommonTextField,
  CommonSelect,
} from "../../Components/ui";

import "./OrgHome.css";

const withTimeout = (promise, ms = 3500) => {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error("Request timed out")), ms)
    ),
  ]);
};

const OrgHome = () => {
  const history = useHistory();
  const [approved, setApproved] = useState({ active: true });
  const [loading, setLoading] = useState(true);
  const [totalLottery, setTotalLottery] = useState(0);
  const [amountEarned, setAmountEarned] = useState("0");
  const [amountLocked, setAmountLocked] = useState("0");
  const [betBalance, setBetBalance] = useState("0");
  const [warn, setWarn] = useState(false);
  const [net, setNet] = useState(false);
  const [isNetVer, setIsNetVer] = useState(true);
  const [creatorAddress, setCreatorAddress] = useState("");
  const [networkId, setNetworkId] = useState(null);

  // Dynamic network currency ticker (e.g. POL, ETH, BNB)
  const currency = useMemo(() => getNetworkCurrency(networkId), [networkId]);

  // Filter & UI State
  const [dateRange, setDateRange] = useState("01 Sep 2026 - 30 Sep 2026");

  // Modal dialog states
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [withdrawModalOpen, setWithdrawModalOpen] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);

  // Filter form state
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterGameType, setFilterGameType] = useState("all");

  useEffect(() => {
    let isMounted = true;

    // Safety timeout: ensure loading skeleton never hangs for more than 2 seconds
    const safetyTimer = setTimeout(() => {
      if (isMounted) {
        setLoading(false);
      }
    }, 2000);

    const initData = async () => {
      try {
        if (!window.ethereum && !web3) {
          if (isMounted) setLoading(false);
          return;
        }

        if (window.ethereum) {
          window.web3 = web3;

          try {
            const netId = await withTimeout(web3.eth.net.getId(), 2500);
            if (isMounted && netId) {
              setNetworkId(netId);
            }
            if (netId === networkID || Number(netId) === Number(networkID)) {
              if (isMounted) setIsNetVer(true);
            } else {
              if (isMounted) {
                setWarn(true);
                setIsNetVer(false);
                setNet(true);
              }
            }
          } catch (netErr) {
            console.warn("Network ID check skipped/timed out:", netErr);
            if (isMounted) setIsNetVer(true);
          }

          let accounts = [];
          try {
            if (window.ethereum.selectedAddress) {
              accounts = [window.ethereum.selectedAddress];
            } else if (window.ethereum.request) {
              accounts = await withTimeout(
                window.ethereum.request({ method: "eth_accounts" }),
                2000
              );
            }
            if (!accounts || accounts.length === 0) {
              accounts = await withTimeout(web3.eth.getAccounts(), 2000);
            }
          } catch (accErr) {
            console.warn("Account retrieval skipped/timed out:", accErr);
          }

          const account = accounts?.[0] || "";
          if (isMounted) setCreatorAddress(account);

          if (account) {
            const tasks = [
              // 1. Total lotteries
              (async () => {
                try {
                  if (cinstance?.methods?.getOrglotteries) {
                    const res = await withTimeout(
                      cinstance.methods.getOrglotteries(account).call(),
                      3000
                    );
                    if (isMounted && res) {
                      setTotalLottery(Array.isArray(res) ? res.length : Number(res) || 0);
                    }
                  }
                } catch (e) {
                  console.warn("Failed to fetch lotteries:", e);
                }
              })(),

              // 2. Organisation approval & details
              (async () => {
                try {
                  if (usercinstance?.methods?.organisationbyaddr) {
                    const res = await withTimeout(
                      usercinstance.methods.organisationbyaddr(account).call(),
                      3000
                    );
                    if (isMounted && res) {
                      setApproved(res);
                    }
                  }
                } catch (e) {
                  console.warn("Failed to fetch organisation details:", e);
                }
              })(),

              // 3. Amount earned
              (async () => {
                try {
                  if (usercinstance?.methods?.amountEarned) {
                    const res = await withTimeout(
                      usercinstance.methods.amountEarned(account).call(),
                      3000
                    );
                    if (isMounted && res !== undefined) {
                      const etherVal = web3.utils.fromWei(`${res}`, "ether");
                      setAmountEarned(etherVal || "0");
                    }
                  }
                } catch (e) {
                  console.warn("Failed to fetch amount earned:", e);
                }
              })(),

              // 4. Amount locked
              (async () => {
                try {
                  if (cinstance?.methods?.amountLocked) {
                    const res = await withTimeout(
                      cinstance.methods.amountLocked(account).call(),
                      3000
                    );
                    if (isMounted && res !== undefined) {
                      const etherVal = web3.utils.fromWei(`${res}`, "ether");
                      setAmountLocked(etherVal || "0");
                    }
                  }
                } catch (e) {
                  console.warn("Failed to fetch amount locked:", e);
                }
              })(),

              // 5. BET Token Balance
              (async () => {
                try {
                  if (tokencinstance?.methods?.balanceOf) {
                    const res = await withTimeout(
                      tokencinstance.methods.balanceOf(account).call(),
                      3000
                    );
                    if (isMounted && res !== undefined) {
                      const betVal = web3.utils.fromWei(`${res}`, "ether");
                      setBetBalance(betVal || "0");
                    }
                  }
                } catch (e) {
                  console.warn("Failed to fetch BET balance:", e);
                }
              })(),
            ];

            await Promise.allSettled(tasks);
          }
        }
      } catch (err) {
        console.error("Dashboard initialization error:", err);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    initData();

    const handleAccountsChanged = (newAccounts) => {
      if (isMounted) {
        if (newAccounts && newAccounts.length > 0) {
          setCreatorAddress(newAccounts[0]);
          initData();
        } else {
          setCreatorAddress("");
          setLoading(false);
        }
      }
    };

    if (window.ethereum?.on) {
      window.ethereum.on("accountsChanged", handleAccountsChanged);
    }

    return () => {
      isMounted = false;
      clearTimeout(safetyTimer);
      if (window.ethereum?.removeListener) {
        window.ethereum.removeListener(
          "accountsChanged",
          handleAccountsChanged
        );
      }
    };
  }, []);

  const referralUrl = `${window?.location?.origin}${Path.org}?ref=${
    creatorAddress || ""
  }`;

  const handleShareReferral = useCallback(() => {
    setShareModalOpen(true);
  }, []);

  const handleCopyReferral = useCallback(() => {
    navigator.clipboard.writeText(referralUrl);
    toast.success("Referral link copied to clipboard!", {
      position: "top-right",
      autoClose: 3000,
    });
  }, [referralUrl]);

  const handleWithdraw = useCallback(async () => {
    try {
      setWithdrawing(true);
      const accounts = await web3.eth.getAccounts();
      if (!accounts?.[0]) {
        toast.error("No connected wallet found.");
        setWithdrawing(false);
        return;
      }

      await cinstance.methods.withdrawcommission().send({ from: accounts[0] });

      toast.success("Withdrawal completed successfully!");
      setWithdrawModalOpen(false);
      window.location.reload();
    } catch (err) {
      console.error("Withdraw error:", err);
      toast.error("Withdrawal cancelled or failed.");
    } finally {
      setWithdrawing(false);
    }
  }, []);

  const metricsData = useMemo(() => {
    return [
      {
        title: "Total Lotteries",
        value: totalLottery || 0,
        trend: { value: "Live", isPositive: true, label: "Campaigns created" },
        tooltip: "Total lottery campaigns you created on AutoBET.",
        chartColor: colors.charts.blue,
        chartData: [8, 12, 10, 15, 14, 18, 22, 25, 28],
      },
      {
        title: "Amount Locked",
        value: `${amountLocked || "0"} ${currency}`,
        trend: { value: "On-Chain", isPositive: true, label: "Currently held" },
        tooltip:
          "Total reserve collateral held on-chain across your lotteries.",
        chartColor: colors.charts.green,
        chartData: [12, 14, 18, 16, 22, 26, 24, 31, 35],
      },
      {
        title: "Amount Received",
        value: `${amountEarned || "0"} ${currency}`,
        trend: {
          value: "Claimable",
          isPositive: true,
          label: "Commission earned",
        },
        tooltip: "Net commission earnings accrued to your creator profile.",
        chartColor: colors.charts.purple,
        chartData: [20, 22, 21, 26, 24, 28, 30, 32, 36],
      },
      {
        title: "BET Balance",
        value: `${betBalance || 0} BET`,
        trend: { value: "Balance", isPositive: true, label: "Platform tokens" },
        tooltip: "AutoBET platform governance & utility token balance.",
        chartColor: colors.charts.cyan,
        chartData: [18, 19, 21, 20, 22, 23, 22, 25, 26],
      },
    ];
  }, [totalLottery, amountLocked, amountEarned, betBalance, currency]);

  // Static structure for middle breakdown cards
  const revenueBreakdownItems = [
    {
      label: "Create Registration Fees",
      amount: "2,750.00",
      percentage: "2.44%",
      icon: <LotteryIcon fontSize="inherit" />,
      iconBg: colors.blue[50],
      iconColor: colors.blue[600],
    },
    {
      label: "Game Creation Fees",
      amount: "3,200.00",
      percentage: "2.84%",
      icon: <GiftCardIcon fontSize="inherit" />,
      iconBg: colors.emerald[50],
      iconColor: colors.emerald[600],
    },
    {
      label: "5% Direct Platform Revenue",
      amount: "56,293.75",
      percentage: "49.96%",
      icon: <TrendingIcon fontSize="inherit" />,
      iconBg: colors.purple[50],
      iconColor: colors.purple[500],
    },
    {
      label: "Partner Revenue Share",
      amount: "40,288.90",
      percentage: "35.77%",
      icon: <SwapIcon fontSize="inherit" />,
      iconBg: colors.teal[50],
      iconColor: colors.teal[600],
    },
    {
      label: "Gateway / Inclusion Fees",
      amount: "11,246.10",
      percentage: "9.99%",
      icon: <WalletIcon fontSize="inherit" />,
      iconBg: colors.blue[50],
      iconColor: colors.blue[500],
    },
  ];

  const positionSummaryItems = [
    { label: "Statutory Gaming Revenue (Gross)", value: "1,250,345.00" },
    { label: "Gaming Net Revenue To Tax", value: "1,162,820.20" },
    { label: "Gaming Machine Tax Accrued", value: "87,524.80" },
    { label: "Tax Deducted (TDS / Other)", value: "0.00" },
    {
      label: "Outstanding Tax Liability",
      value: "87,524.80",
      isOutstanding: true,
      hasDivider: true,
    },
    { label: "Tax Held in Management Wallet", value: "87,524.80" },
    {
      label: "Next Settlement / Filing Date",
      value: "30 Sep 2026 (Q3 2026)",
      rawPrefix: "",
      isBold: true,
    },
  ];

  const defaultTableRows = [
    {
      id: "1",
      game: "5-CardBET #1254",
      creator: "GALAXit",
      revenue: "125,000.00",
      payouts: "91,750.00",
      tax: "6,250.00",
      netRevenue: "18,000.00",
      status: "Active",
    },
    {
      id: "2",
      game: "Spin & Win #9851",
      creator: "Yamata Gaming",
      revenue: "96,563.00",
      payouts: "71,929.00",
      tax: "4,828.15",
      netRevenue: "13,805.85",
      status: "Active",
    },
    {
      id: "3",
      game: "SlotBET #6781",
      creator: "Jackpot Legends",
      creatorColor: colors.error.main,
      revenue: "60,450.00",
      payouts: "50,375.00",
      tax: "3,022.50",
      netRevenue: "7,052.50",
      status: "Active",
    },
    {
      id: "4",
      game: "MinBET #4317",
      creator: "PKL Play",
      revenue: "32,400.00",
      payouts: "30,150.00",
      tax: "2,158.00",
      netRevenue: "218.00",
      status: "Active",
    },
    {
      id: "5",
      game: "Number 4T10",
      creator: "Lucky 777",
      revenue: "18,100.00",
      payouts: "14,000.00",
      tax: "906.00",
      netRevenue: "3,194.00",
      status: "Active",
    },
  ];

  const quickActionsList = [
    {
      label: "Create New Lottery",
      icon: (
        <LotteryIcon sx={{ color: colors.primary.main, fontSize: "1.1rem" }} />
      ),
      path: Path.orgcreatelottery,
    },
    {
      label: "Create Gift Card",
      icon: (
        <GiftCardIcon sx={{ color: colors.primary.main, fontSize: "1.1rem" }} />
      ),
      path: Path.orgcreatecard,
    },
    {
      label: "My Lotteries & Analytics",
      icon: (
        <StarsIcon sx={{ color: colors.primary.main, fontSize: "1.1rem" }} />
      ),
      path: Path.orgmylottery,
    },
    {
      label: "Share Referral Link",
      icon: (
        <ShareIcon sx={{ color: colors.primary.main, fontSize: "1.1rem" }} />
      ),
      action: handleShareReferral,
    },
  ];

  if (warn) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: colors.background.default,
          p: 3,
        }}
      >
        <Container maxWidth="sm">
          <Box
            sx={{
              p: 4,
              textAlign: "center",
              borderRadius: borderRadiusPx.xl,
              backgroundColor: colors.background.paper,
              boxShadow: customShadows.dialog,
              border: `1px solid ${colors.dashboard.cardBorder}`,
            }}
          >
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, color: colors.slate[900], mb: 2 }}
            >
              Network Connection Required
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: colors.slate[500], mb: 3 }}
            >
              {net
                ? `Please connect to the correct network (Network ID: ${networkID}) in your Web3 wallet.`
                : "Please install an Ethereum-compatible browser extension like MetaMask to access the Creator."}
            </Typography>
            <CommonButton
              variant="contained"
              onClick={() => history.push(Path.help)}
            >
              Get Help
            </CommonButton>
          </Box>
        </Container>
      </Box>
    );
  }

  if (isNetVer && approved && !approved.active && !loading) {
    return <RegisterAsorganisation />;
  }

  return (
    <CreatorLayout
      title="Creator Dashboard"
      subtitle="Real-time performance and analytics for your hosted campaigns"
      breadcrumbs={[{ label: "Dashboard" }]}
      headerType="dashboard"
      dateRangeText={dateRange}
      onDateRangeChange={(range) => setDateRange(range)}
      onFilterClick={() => setFilterModalOpen(true)}
      creatorAddress={creatorAddress}
      userName={approved?.name || "Creator"}
      userRole="Operator"
      authChecked={!loading}
      isAuthorized={Boolean(approved?.active)}
      accessDeniedProps={{
        title: "Account Access Denied",
        message: "You must be a registered Creator or organization to view Creator Dashboard.",
        actionText: "Register as Organisation",
        actionPath: Path.registerasorg,
      }}
    >
      <Stack spacing={3.5}>
        {/* Previously Mapped Core Creator Metric Cards Grid (4 Cards) */}
            <Box>
              <Grid container spacing={2.5}>
                {metricsData.map((metric, index) => (
                  <Grid item xs={12} sm={6} md={3} key={index}>
                    <MetricCard
                      title={metric.title}
                      value={metric.value}
                      trend={metric.trend}
                      tooltip={metric.tooltip}
                      chartColor={metric.chartColor}
                      chartData={metric.chartData}
                      loading={loading}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* Notification / Regulatory Trust Banner */}
            <DashboardNoticeBanner
              message="Note: Gaming Tax Liability is statutory amount held in trust & no TDS/GST/other tax is withheld as per regulation. This is not AutoBET income."
              actionText="View Tax Management"
              onActionClick={() => history.push(Path.orgmylottery)}
            />

            {/* Middle 4-Column Analytical Grid */}
            <Box>
              <Grid container spacing={2.5}>
                {/* Column 1: Revenue Breakdown */}
                <Grid item xs={12} sm={6} md={6} lg={6} xl={3}>
                  <RevenueBreakdownCard
                    title="AutoBET Platform Revenue Breakdown"
                    items={revenueBreakdownItems}
                    totalLabel="Total AutoBET Platform Revenue"
                    totalAmount="112,589.75"
                    totalPercentage="100%"
                  />
                </Grid>

                {/* Column 2: Position Summary */}
                <Grid item xs={12} sm={6} md={6} lg={6} xl={3}>
                  <PositionSummaryCard
                    title="Tax & Regulatory Position"
                    items={positionSummaryItems}
                  />
                </Grid>

                {/* Column 3: Tax Management Wallet */}
                <Grid item xs={12} sm={6} md={6} lg={6} xl={3}>
                  <WalletTreasuryCard
                    title="Tax Management Wallet"
                    walletLabel="Online - PKL081"
                    walletAddress={creatorAddress}
                    balance="87,524.80"
                    collected="87,524.80"
                    remitted="0.00"
                    outstandingLiability="87,524.80"
                    statusBadge="AWAITING LIABILITY PAYMENT"
                    statusColor={
                      colors.dashboard.status.pendingText || "#b45309"
                    }
                    statusBg={colors.dashboard.status.pendingBg || "#fef3c7"}
                    nextReview="Quarterly"
                    currencyPrefix="K "
                    onViewTransactions={() =>
                      history.push(Path.orgmylottery)
                    }
                    onPrimaryAction={() =>
                      toast.info("Generating Tax Report...")
                    }
                    primaryActionLabel="Generate Tax Report"
                    secondaryActionLabel="View Transactions"
                  />
                </Grid>

                {/* Column 4: Creator Hosted Summary & Quick Actions */}
                <Grid item xs={12} sm={6} md={6} lg={6} xl={3}>
                  <CreatorSummaryActionsCard
                    title="Creator Hosted Games"
                    stats={{
                      gamesCreated: totalLottery || 0,
                      grossRevenue: "0.00",
                      netRevenue: "0.00",
                    }}
                    statementLinkText="View Creator Statement →"
                    statementLinkTo={Path.orgmylottery}
                    quickActions={quickActionsList}
                    noticeTitle="Statutory Notice:"
                    noticeMessage="All gaming operations are secured via smart contract escrow with verifiable on-chain randomness."
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Top Performing Games / Lotteries Table Card */}
            <TopPerformingTableCard
              title="Top Performing Games"
              rows={defaultTableRows}
              viewAllText="View All Games Financial Statement"
              onViewAllClick={() => history.push(Path.orgmylottery)}
            />

            {/* Dark Footer Ecosystem Metrics Ribbon */}
            <BottomMetricsRibbon
              metrics={[
                {
                  label: "Total Creators",
                  value: "156",
                  delta: "7 this month",
                  isPositive: true,
                },
                {
                  label: "Total Games",
                  value: totalLottery > 0 ? `${totalLottery}` : "342",
                  delta: "14 this month",
                  isPositive: true,
                },
                {
                  label: "Active Players",
                  value: "18,924",
                  delta: "1,243 this month",
                  isPositive: true,
                },
                {
                  label: "Total Bets",
                  value: "245,678",
                  delta: "15,678 this month",
                  isPositive: true,
                },
                {
                  label: "Smart Wallet Txs",
                  value: "128,540",
                  delta: "8,345 this month",
                  isPositive: true,
                },
                {
                  label: `Gas Spent (${currency})`,
                  value: `4.5281 ${currency}`,
                  delta: "0.521 this month",
                  isPositive: true,
                },
                {
                  label: "Avg Gas Cost / Txn",
                  value: "$0.00018",
                  delta: "-0.00002 this month",
                  isPositive: false,
                },
              ]}
            />
          </Stack>

      {/* Filter Modal Dialog */}
      <CommonDialog
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        title="Dashboard Filters"
        maxWidth="xs"
        actions={
          <>
            <CommonButton
              variant="reset"
              onClick={() => setFilterModalOpen(false)}
            >
              Reset
            </CommonButton>
            <CommonButton
              variant="contained"
              onClick={() => {
                setFilterModalOpen(false);
                toast.info("Filters applied!");
              }}
            >
              Apply Filters
            </CommonButton>
          </>
        }
      >
        <Stack spacing={2.5}>
          <CommonSelect
            label="Status"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            options={[
              { value: "all", label: "All Campaigns" },
              { value: "active", label: "Active Only" },
              { value: "pending", label: "Pending Draw" },
              { value: "ended", label: "Completed / Ended" },
            ]}
          />

          <CommonSelect
            label="Game Type"
            value={filterGameType}
            onChange={(e) => setFilterGameType(e.target.value)}
            options={[
              { value: "all", label: "All Game Types" },
              { value: "lottery", label: "Standard Lottery" },
              { value: "spin", label: "Spin & Win" },
              { value: "giftcard", label: "Gift Cards" },
            ]}
          />
        </Stack>
      </CommonDialog>

      {/* Withdraw Balance Dialog */}
      <CommonDialog
        open={withdrawModalOpen}
        onClose={() => !withdrawing && setWithdrawModalOpen(false)}
        title="Withdraw Creator Balance"
        maxWidth="xs"
        actions={
          <>
            <CommonButton
              variant="cancel"
              disabled={withdrawing}
              onClick={() => setWithdrawModalOpen(false)}
            >
              Cancel
            </CommonButton>
            <CommonButton
              variant="contained"
              disabled={withdrawing || Number(amountEarned) <= 0}
              loading={withdrawing}
              onClick={handleWithdraw}
              sx={{
                backgroundColor: colors.emerald[500],
                "&:hover": { backgroundColor: colors.emerald[600] },
              }}
            >
              Confirm Withdraw
            </CommonButton>
          </>
        }
      >
        <Box sx={{ textAlign: "center", mb: 2 }}>
          <Typography
            variant="body2"
            sx={{ color: colors.slate[500], mb: 0.5 }}
          >
            Available Claimable Balance:
          </Typography>
          <Typography
            variant="h4"
            sx={{ fontWeight: 800, color: colors.emerald[500] }}
          >
            {amountEarned} {currency}
          </Typography>
        </Box>
        <Typography
          variant="caption"
          sx={{
            color: colors.slate[400],
            display: "block",
            textAlign: "center",
          }}
        >
          Withdrawal transactions are executed directly through the on-chain
          smart contract to your connected wallet.
        </Typography>
      </CommonDialog>

      {/* Share Referral Modal */}
      <CommonDialog
        open={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        title="Share Creator Referral Link"
        maxWidth="sm"
        actions={
          <CommonButton
            variant="cancel"
            onClick={() => setShareModalOpen(false)}
          >
            Done
          </CommonButton>
        }
      >
        <Typography variant="body2" sx={{ color: colors.slate[500], mb: 2 }}>
          Invite other creators to AutoBET. You earn referral revenue share on
          their hosted campaigns!
        </Typography>

        <Stack direction="row" spacing={1} alignItems="center">
          <CommonTextField
            value={referralUrl}
            readOnly
            InputProps={{
              sx: { fontFamily: "monospace", fontSize: "0.82rem" },
            }}
          />
          <CommonButton
            variant="contained"
            startIcon={<CopyIcon />}
            onClick={handleCopyReferral}
            sx={{ flexShrink: 0, px: 2 }}
          >
            Copy
          </CommonButton>
        </Stack>
      </CommonDialog>
    </CreatorLayout>
  );
};

export default OrgHome;
